import React, { useEffect, useRef, useState } from "react";
import { apiJson, getToken } from "@/api/client.js";
import { PWSGroupMap } from "@/components/PWSGroupMap.jsx";
import { PWSWorkModal } from "@/components/PWSWorkModal.jsx";
import { MAP_CHOICES } from "@/content/sepaqSheet.js";

const ORDER = ["Luc", "Éric", "Marcel", "Pierre", "Steve", "Dan"];
const STEPS = [
  ["Comparer les cartes", "Voir les nouveaux points et les différences"],
  ["Créer la carte de demain", "Générer la nouvelle version"],
  ["La rendre disponible aux chasseurs", "Exporter et partager"],
];

function formula(baseCode, published, added, removed, nextCode, total) {
  const addedWord = added === 1 ? "nouveau" : "nouveaux";
  const removedWord = removed > 1 ? "retirés" : "retiré";
  return `V${baseCode} ${published} + ${added} ${addedWord} − ${removed} ${removedWord} = V${nextCode} ${total}`;
}

function clock() {
  return new Intl.DateTimeFormat("fr-CA", {
    timeZone: "America/Toronto",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date());
}

async function downloadFile(code, ext) {
  const token = getToken();
  const res = await fetch(`/api/nordquest/group/file/${code}/${ext}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) return false;
  const blob = await res.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `Carte-du-groupe-V${code}.${ext}`;
  link.click();
  URL.revokeObjectURL(link.href);
  return true;
}

export function TomorrowDesk({ group, points = [], onReload }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState("");
  const [menu, setMenu] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [share, setShare] = useState(false);
  const [ready, setReady] = useState(null);
  const [madeAt, setMadeAt] = useState("");
  const [toast, setToast] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [basemap, setBasemap] = useState("official");
  const [layersOpen, setLayersOpen] = useState(false);
  const [card, setCard] = useState(null);
  const [anchor, setAnchor] = useState(null);
  const mapRef = useRef(null);
  const listRef = useRef(null);
  const fitted = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  useEffect(() => {
    if (!open) {
      fitted.current = false;
      return undefined;
    }
    if (fitted.current) return undefined;
    const frame = (points || []).filter((point) => Number.isFinite(Number(point.latitude)) && Number.isFinite(Number(point.longitude)) && !point.aside);
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      const box = document.querySelector("[data-testid='tomorrow-map']");
      if (!box || box.clientHeight < 200) return;
      mapRef.current?.invalidate?.();
      const ok = mapRef.current?.fitPoints?.(frame);
      const zoom = mapRef.current?.getZoom?.() || 0;
      if (ok && zoom >= 11) {
        fitted.current = true;
        window.clearInterval(timer);
      } else if (tries > 20) window.clearInterval(timer);
    }, 200);
    return () => window.clearInterval(timer);
  }, [open, points]);

  if (!group?.is_admin) return null;

  const preview = group.preview || {};
  const news = preview.new_points || [];
  const published = preview.published_version_count ?? preview.base_count ?? 9;
  const added = preview.new_count ?? news.length;
  const removed = preview.removed_count ?? 0;
  const modified = preview.modified_count ?? 0;
  const total = preview.current_active_count ?? preview.total ?? (published + added - removed);
  const baseCode = preview.base_version || "1";
  const nextCode = preview.next_code || "2";
  const draft = ready || group.draft;
  const hunters = [...(group.hunters || [])].sort((a, b) => ORDER.indexOf(a.first_name) - ORDER.indexOf(b.first_name));
  const changed = added + removed + modified > 0;
  const activeStep = draft?.released ? 3 : draft ? 2 : step;

  function owned(hunter) {
    return news.filter((point) => point.author_id === hunter.member_id || point.author === hunter.first_name);
  }

  function ping(message) {
    setToast(message);
    window.setTimeout(() => setToast(""), 2600);
  }

  function showPoint(point) {
    const id = point.point_id || point.id || point.key || "";
    const match = (points || []).find((row) => row.id === id) || point;
    setSelected(match.id || id);
    setCard(match);
    setFilter("all");
    setMenu("");
    const lat = Number(match.latitude);
    const lng = Number(match.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      mapRef.current?.focus?.(lat, lng);
      window.setTimeout(() => setAnchor(mapRef.current?.anchor?.(lat, lng) || null), 500);
    }
    window.setTimeout(() => mapRef.current?.invalidate?.(), 80);
    listRef.current?.querySelector(`[data-point="${id}"]`)?.scrollIntoView({ block: "nearest" });
    ping(`Point #${match.number || "?"} sur la carte`);
  }

  function askCreate() {
    if (draft || !changed) return;
    setStep(1);
    setConfirm(true);
  }

  async function createVersion() {
    setConfirm(false);
    try {
      const body = await apiJson("/api/nordquest/group/publish", { ready: true }, "POST");
      if (body?.code) {
        setReady(body);
        setMadeAt(clock());
        setStep(2);
        ping(`Carte V${body.code} prête. ${body.point_count} points au total.`);
        onReload?.();
      }
    } catch {
      ping("La carte n’a pas été créée. V1 reste disponible.");
    }
  }

  async function releaseHunters() {
    try {
      const body = await apiJson("/api/nordquest/group/release", {}, "POST");
      if (body?.code) setReady({ ...draft, ...body, released: true });
      setShare(false);
      ping("V2 disponible pour le groupe");
      onReload?.();
    } catch {
      ping("La carte n’a pas été rendue disponible. V1 reste en place.");
    }
  }

  async function copyLink() {
    const url = window.location.origin + "/";
    try {
      await navigator.clipboard.writeText(url);
      ping("Lien copié");
    } catch {
      ping(url);
    }
  }

  async function saveKml() {
    if (!draft?.code) return;
    const ok = await downloadFile(draft.code, "kml");
    ping(ok ? "Fichier téléchargé" : "Le fichier n’est pas prêt.");
  }

  const freshIds = new Set(news.map((point) => point.point_id || point.key));
  const mapPoints = (points || [])
    .filter((point) => Number.isFinite(Number(point.latitude)) && Number.isFinite(Number(point.longitude)))
    .filter((point) => !point.aside)
    .filter((point) => {
      const fresh = freshIds.has(point.id);
      if (filter === "today") return fresh;
      if (filter === "v1") return !fresh;
      return true;
    })
    .map((point) => ({ ...point, tonight: freshIds.has(point.id) }));

  return (
    <section data-testid="map-evening">
      <button type="button" className="og-btn og-btn--primary" data-testid="prepare-tomorrow" onClick={() => setOpen(true)}>Préparer la carte de demain</button>
      {open ? (
        <PWSWorkModal stacked size="map" skin="tomorrow" title="CARTE DE DEMAIN" subtitle="La carte actuelle des gars est comparée automatiquement avec tous les points présents maintenant dans Orignal2026." onClose={() => setOpen(false)} testId="tomorrow-desk" closeTestId="tomorrow-close">
          <div className="og-td">
            <div className="og-td-scroll">
              <p className="og-td-lead">Préparer la carte de demain</p>
              {toast ? <p className="og-td-toast" role="status" data-testid="tomorrow-toast">{toast}</p> : null}
              <ol className="og-td-steps" data-testid="tomorrow-steps">
                {STEPS.map(([title, hint], index) => {
                  const done = activeStep > index;
                  const on = activeStep === index;
                  return (
                    <li key={title}>
                      <button type="button" className={`og-td-step${on ? " is-on" : ""}${done ? " is-done" : ""}`} onClick={() => { if (index === 2 && !draft) { ping("Crée d’abord la carte."); return; } setStep(index); }}>
                        <span className="og-td-num">{done ? "✓" : index + 1}</span>
                        <strong>{title}</strong>
                        <em>{hint}</em>
                      </button>
                    </li>
                  );
                })}
              </ol>
              <div className="og-td-balance" data-testid="tomorrow-balance" data-published={published} data-current={total} data-new={added} data-removed={removed} data-modified={modified}>
                <article data-testid="tomorrow-current"><h3>Carte du matin</h3><strong>V{baseCode}</strong><p>{published} points</p></article>
                <span className="og-td-arrow" aria-hidden="true">→</span>
                <article className="is-add" data-testid="tomorrow-review"><h3>Ajoutés depuis</h3><strong>+{added}</strong><p>{added === 1 ? "nouveau point" : "nouveaux points"}</p></article>
                <span className="og-td-arrow" aria-hidden="true">→</span>
                <article data-testid="tomorrow-result"><h3>Carte de demain</h3><strong>V{nextCode}</strong><p>{total} points</p></article>
              </div>
              <p data-testid="tomorrow-formula">{formula(baseCode, published, added, removed, nextCode, total)}</p>
              {modified ? <p data-testid="tomorrow-modified">{modified === 1 ? "1 point modifié" : `${modified} points modifiés`}</p> : null}
              {step === 1 && !draft ? (
                <section className="og-td-summary" data-testid="tomorrow-summary">
                  <p>Carte actuelle</p><strong>V{baseCode} · {published} points</strong>
                  <p>Ajouts</p><strong>+{added}</strong>
                  <p>Retraits</p><strong>{removed}</strong>
                  <p>Résultat</p><strong>V{nextCode} · {total} points</strong>
                </section>
              ) : null}
              {draft ? (
                <section data-testid="tomorrow-ready">
                  <h3>V{draft.code} PRÊTE ✓</h3>
                  <p>{draft.point_count} points</p>
                  <p>{draft.new_count} nouveaux</p>
                  <p>Fichier KML généré</p>
                  <p>{madeAt}</p>
                  <p>{draft.kml || `Orignal2026_Groupe_V${draft.code}.kml`}</p>
                </section>
              ) : null}
              <div className={`og-td-cols${expanded ? " is-wide" : ""}`}>
                <div ref={listRef}>
                  <h3>Nouveaux depuis V{baseCode} ({added})</h3>
                  <ul className="og-td-authors">
                    {hunters.map((hunter) => (
                      <li key={hunter.member_id} data-testid={`tomorrow-${hunter.member_id}`}>{hunter.first_name} — {owned(hunter).length}</li>
                    ))}
                  </ul>
                  {news.length ? news.map((point, index) => {
                    const id = point.point_id || point.key || String(index);
                    return (
                      <article key={id} className={`og-td-card${selected === id ? " is-on" : ""}`} data-testid={`review-point-${index}`} data-point={id}>
                        <div className="og-td-thumb" aria-hidden="true"><span>#{point.number || "?"}</span></div>
                        <div>
                          <strong>Point #{point.number || "?"} — {point.name}</strong>
                          <span className="og-td-badge">Nouveau</span>
                          <p>{point.author || "À confirmer"}</p>
                          {point.note ? <p>{point.note}</p> : null}
                          <div className="og-td-card__actions">
                            <button type="button" className="og-btn" data-testid={`see-point-${index}`} onClick={() => showPoint(point)}>Voir sur la carte</button>
                            <button type="button" className="og-btn" aria-label={`Menu du point ${point.number || index}`} data-testid={`point-menu-${index}`} onClick={() => setMenu(menu === id ? "" : id)}>⋯</button>
                          </div>
                          {menu === id ? <p>{Number(point.latitude).toFixed(5)}, {Number(point.longitude).toFixed(5)}</p> : null}
                        </div>
                      </article>
                    );
                  }) : <p>Aucun nouveau point depuis la carte du matin.</p>}
                  {(preview.removed_points || []).length ? (
                    <section>
                      <h3>Retirés</h3>
                      {preview.removed_points.map((point) => <p key={point.key || point.name}>Point #{point.number || "?"} — {point.name}</p>)}
                    </section>
                  ) : null}
                  {step === 0 ? <button type="button" className="og-btn og-btn--primary" data-testid="tomorrow-continue" onClick={() => setStep(1)}>Continuer</button> : null}
                </div>
                <div>
                  <div className="og-td-maphead">
                    <h3>Carte de demain — aperçu</h3>
                    <button type="button" className="og-btn" data-testid="map-expand" onClick={() => { setExpanded((value) => !value); window.setTimeout(() => mapRef.current?.invalidate?.(), 80); }}>{expanded ? "Réduire" : "Agrandir"}</button>
                  </div>
                  <div className="og-td-filters">
                    {[["all", "Tous"], ["v1", "Carte V1"], ["today", "Nouveaux"]].map(([id, label]) => (
                      <button key={id} type="button" className={`og-btn${filter === id ? " is-on" : ""}`} data-testid={`tomorrow-filter-${id}`} onClick={() => setFilter(id)}>{label}</button>
                    ))}
                    <button type="button" className={`og-btn${layersOpen ? " is-on" : ""}`} data-testid="tomorrow-layers" onClick={() => setLayersOpen((value) => !value)}>Couches</button>
                  </div>
                  {layersOpen ? (
                    <div className="og-td-layers" data-testid="tomorrow-layer-list">
                      {MAP_CHOICES.map((choice) => (
                        <button key={choice.id} type="button" className={`og-btn${basemap === choice.id ? " is-on" : ""}`} data-testid={`tomorrow-layer-${choice.id}`} onClick={() => setBasemap(choice.id)}>{choice.label}</button>
                      ))}
                    </div>
                  ) : null}
                  <div className={`og-td-map${expanded ? " is-wide" : ""}`} data-basemap={basemap} data-count={mapPoints.length}>
                    <PWSGroupMap
                      ref={mapRef}
                      basemap={basemap}
                      points={mapPoints}
                      selectedId={selected}
                      onSelect={(point) => {
                        setSelected(point.id);
                        setCard(point);
                        const lat = Number(point.latitude);
                        const lng = Number(point.longitude);
                        if (Number.isFinite(lat) && Number.isFinite(lng)) {
                          mapRef.current?.focus?.(lat, lng);
                          window.setTimeout(() => setAnchor(mapRef.current?.anchor?.(lat, lng) || null), 500);
                        }
                        listRef.current?.querySelector(`[data-point="${point.id}"]`)?.scrollIntoView({ block: "nearest" });
                        ping(`Point #${point.number || "?"} sélectionné`);
                      }}
                      onBlank={() => { setCard(null); setAnchor(null); }}
                      testId="tomorrow-map"
                    />
                    <div className="og-mapchrome" aria-hidden="true">
                      <span className="og-compass">N</span>
                      {basemap === "official" ? <span className="og-sepaqbadge">Sépaq</span> : null}
                    </div>
                    {card ? (
                      <aside
                        className={`og-spotfloat${anchor ? " is-placed" : ""}${anchor && anchor.width && anchor.x > anchor.width * 0.62 ? " is-left" : ""}`}
                        data-testid="tomorrow-sheet"
                        style={anchor ? { left: anchor.x, top: anchor.y } : undefined}
                      >
                        <button type="button" className="og-spotfloat__x" data-testid="tomorrow-sheet-close" onClick={() => { setCard(null); setAnchor(null); }} aria-label="Fermer la fiche">×</button>
                        <h3>#{card.number} — {card.name}</h3>
                        <p>{card.author || "À confirmer"}</p>
                        {card.note ? <p>{card.note}</p> : null}
                        {card.tonight || freshIds.has(card.id) ? <p>Nouveau depuis V{baseCode}</p> : null}
                        <p>{Number(card.latitude).toFixed(5)}, {Number(card.longitude).toFixed(5)}</p>
                      </aside>
                    ) : null}
                  </div>
                </div>
              </div>
              <p className="og-td-note">Une fois V2 créée, le fichier KML de la nouvelle carte sera prêt pour NordQuest.</p>
              {draft ? (
                <p>
                  <button type="button" className="og-btn" onClick={() => setAdvanced((value) => !value)}>Options avancées</button>
                  {advanced ? <button type="button" className="og-btn" onClick={() => downloadFile(draft.code, "gpx")}>Télécharger le GPX</button> : null}
                </p>
              ) : null}
            </div>
            <footer className="og-td-dock">
              <button type="button" className="og-btn" data-testid="tomorrow-back" onClick={() => { if (step > 0 && !draft?.released) setStep(step - 1); else setOpen(false); }}>Retour</button>
              <button type="button" className="og-btn og-btn--primary" data-testid="create-v2" disabled={!changed || Boolean(draft)} onClick={askCreate}>Créer V{nextCode} — {total} points</button>
              <button type="button" className="og-btn" data-testid="tomorrow-download" disabled={!draft?.code} title={draft?.code ? "Télécharger le KML" : "Disponible une fois la carte créée"} onClick={saveKml}>Télécharger KML</button>
              <button type="button" className="og-btn" data-testid="publish-hunters" disabled={!draft?.code || Boolean(draft?.released)} title={draft?.code ? "Rendre la carte disponible" : "Disponible une fois la carte créée"} onClick={() => setShare(true)}>Rendre disponible aux chasseurs</button>
            </footer>
          </div>
        </PWSWorkModal>
      ) : null}
      {confirm ? (
        <PWSWorkModal stacked title={`Créer V${nextCode} ?`} onClose={() => setConfirm(false)} testId="tomorrow-confirm" closeTestId="tomorrow-confirm-close">
          <p>V{baseCode} restera disponible.</p>
          <p>La nouvelle carte contiendra {total} points.</p>
          <p>Carte actuelle V{baseCode} : {published} points</p>
          <p>Nouveaux : +{added}</p>
          <p>Retirés : {removed}</p>
          <div className="og-now__actions">
            <button type="button" className="og-btn" data-testid="create-v2-cancel" onClick={() => setConfirm(false)}>Annuler</button>
            <button type="button" className="og-btn og-btn--primary" data-testid="create-v2-confirm" onClick={createVersion}>Créer V{nextCode}</button>
          </div>
        </PWSWorkModal>
      ) : null}
      {share && draft ? (
        <PWSWorkModal stacked title={`Carte V${draft.code} disponible pour le groupe`} onClose={() => setShare(false)} testId="tomorrow-share" closeTestId="tomorrow-share-close">
          <p>V{draft.code}</p>
          <p>{draft.point_count} points</p>
          <p>{draft.new_count} nouveaux</p>
          <ul>
            {hunters.map((hunter) => (
              <li key={hunter.member_id} data-testid={`dist-${hunter.member_id}`}>{hunter.first_name} — {hunter.installed === draft.code ? "Installée" : "À installer"}</li>
            ))}
          </ul>
          <div className="og-now__actions">
            <button type="button" className="og-btn og-btn--primary" data-testid="publish-v2" onClick={releaseHunters}>Publier V{draft.code} aux chasseurs</button>
            <button type="button" className="og-btn" data-testid="copy-map-link" onClick={copyLink}>Copier lien</button>
            <button type="button" className="og-btn" onClick={saveKml}>Télécharger KML</button>
          </div>
        </PWSWorkModal>
      ) : null}
    </section>
  );
}
